/* eslint-disable prefer-object-spread, default-param-last,
react/function-component-definition, react/require-default-props
*/
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Grid from '@mui/material/Grid';
import { withStyles } from '@mui/styles';

import PanelViewer from './components/panel/index';
import CmdBar from './components/cmd_bar/index';
import LayerContent from './layer_content';
import { LIST_UI_VIEWER_TYPE } from './constants/list_ui';
import { extractParams } from './helpers/extractParams';
import { entitySignature } from './helpers/entity_signature';
import { LIST_HOST_HOOK_CLASS } from './constants/list_graph';

const styles = () => ({
});

const getThresholdState = (state) => {
  const curveIdx = state?.curve?.curveIdx;
  const thresholdList = state?.threshold?.list;
  if (!Array.isArray(thresholdList)) return {};
  if (!Number.isInteger(curveIdx)) return thresholdList[0] || {};
  return thresholdList[curveIdx] || thresholdList[0] || {};
};

// A host that rebuilds `entity` as a fresh object literal on every render (no
// memoization on its side) must not hand ViewerLine (via extractParams'
// `feature`) a brand-new object every single time regardless of whether the
// underlying data changed - ViewerLine.normChange dispatches MANAGER.RESETALL
// whenever `feature`'s reference changes, and that cascades into a fresh
// state.threshold reference, causing LayerPrism to re-render and rebuild
// `feature` again, and so on, once per host render.
//
// This signature gates what actually gets RENDERED, so it must not collide
// between two genuinely different entities; see helpers/entity_signature for
// why it digests every spectra block on both axes rather than just the first
// block's x values.
const useExtractedParams = (entity, thresSt, scanSt) => {
  const signature = `${entitySignature(entity)}|${JSON.stringify(thresSt)}|${JSON.stringify(scanSt)}`;
  const cacheRef = useRef(null);
  if (!cacheRef.current || cacheRef.current.signature !== signature) {
    cacheRef.current = { signature, result: extractParams(entity, thresSt, scanSt) };
  }
  return cacheRef.current.result;
};

const LayerPrism = ({
  entity, cLabel, xLabel, yLabel, forecast, operations,
  descriptions, molSvg, editorOnly, exactMass,
  thresSt, scanSt, uiSt, curveSt, integrationSt,
  canChangeDescription, onDescriptionChanged, entityFileNames, userManualLink,
}) => {
  const {
    topic, feature, hasEdit, integration: initialIntegration, features,
  } = useExtractedParams(entity, thresSt, scanSt);
  if (!topic) return null;

  const curveIdx = (curveSt && Number.isFinite(curveSt.curveIdx)) ? curveSt.curveIdx : 0;
  const liveIntegrations = (integrationSt && Array.isArray(integrationSt.integrations))
    ? integrationSt.integrations
    : null;
  const liveIntegration = liveIntegrations ? liveIntegrations[curveIdx] : null;
  const integration = liveIntegration || initialIntegration;

  const { viewer } = uiSt;
  if (viewer === LIST_UI_VIEWER_TYPE.ANALYSIS) {
    return (
      <div>
        <CmdBar
          feature={feature}
          hasEdit={hasEdit}
          forecast={forecast}
          operations={operations}
          editorOnly={editorOnly}
        />
        <div className={LIST_HOST_HOOK_CLASS.EDITOR_ROOT}>
          <Grid container>
            <Grid item xs={12}>
              <LayerContent
                topic={topic}
                feature={feature}
                features={features || []}
                cLabel={cLabel}
                xLabel={xLabel}
                yLabel={yLabel}
                forecast={forecast}
                operations={operations}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CmdBar
        feature={feature}
        hasEdit={hasEdit}
        forecast={forecast}
        operations={operations}
        editorOnly={editorOnly}
      />
      <div className={LIST_HOST_HOOK_CLASS.EDITOR_ROOT}>
        <Grid container>
          <Grid item xs={9}>
            <LayerContent
              topic={topic}
              feature={feature}
              features={features || []}
              cLabel={cLabel}
              xLabel={xLabel}
              yLabel={yLabel}
              forecast={forecast}
              operations={operations}
            />
          </Grid>
          <Grid item xs={3} align="center">
            <PanelViewer
              feature={feature}
              integration={integration}
              editorOnly={editorOnly}
              molSvg={molSvg}
              exactMass={exactMass}
              entityFileNames={entityFileNames}
              userManualLink={userManualLink}
              descriptions={descriptions}
              canChangeDescription={canChangeDescription}
              onDescriptionChanged={onDescriptionChanged}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => ( // eslint-disable-line
  {
    scanSt: state.scan,
    thresSt: getThresholdState(state),
    uiSt: state.ui,
    curveSt: state.curve,
    integrationSt: state.integration.present,
  }
);

const mapDispatchToProps = (dispatch) => (
  bindActionCreators({
  }, dispatch)
);

LayerPrism.propTypes = {
  entity: PropTypes.object.isRequired,
  cLabel: PropTypes.string.isRequired,
  xLabel: PropTypes.string.isRequired,
  yLabel: PropTypes.string.isRequired,
  molSvg: PropTypes.string.isRequired,
  editorOnly: PropTypes.bool.isRequired,
  exactMass: PropTypes.string,
  forecast: PropTypes.object.isRequired,
  operations: PropTypes.array.isRequired,
  descriptions: PropTypes.array.isRequired,
  thresSt: PropTypes.object.isRequired,
  scanSt: PropTypes.object.isRequired,
  uiSt: PropTypes.object.isRequired,
  curveSt: PropTypes.object.isRequired,
  integrationSt: PropTypes.object.isRequired,
  canChangeDescription: PropTypes.bool.isRequired,
  onDescriptionChanged: PropTypes.func,
  entityFileNames: PropTypes.array,
  userManualLink: PropTypes.object,
};

export default connect( // eslint-disable-line
  mapStateToProps, mapDispatchToProps,
)(withStyles(styles)(LayerPrism));  // eslint-disable-line
